import {
  ApiOutlined,
  MessageOutlined,
  PlusCircleOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import {
  Button,
  Empty,
  Select,
  Switch,
  TreeSelect,
  Typography,
  message,
} from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { store } from "../../../redux/configStore";
import { getListTrademark } from "../../../api/Trademark/Trademark";
import {
  deleteTrademarkCompanyMap,
  getAllTrademarkCompanyMap,
  updateTrademarkCompanyMapStatus,
} from "../../../api/Trademark/TrademarkCompanyMap";
import useStoreData from "../../../hooks/useStoreData";
import { transformData } from "../../../utils/recursive";
import FormAddTrademarkCompanyMapping from "./FormAddTrademarkCompanyMapping";
import RowActions from "../../../components/cms/RowActions";
import { ACTION_ICON, confirmDanger } from "../../../components/cms/actions";
import StatusTag from "../../../components/cms/StatusTag";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import StatStrip from "../../../components/cms/StatStrip";
import ListToolbar from "../../../components/cms/ListToolbar";
import CmsTableCard from "../../../components/cms/CmsTableCard";
import { brand, semantic } from "../../../theme/tokens";

const { Text } = Typography;

const FILTER_DEFS = [
  { key: "branch", label: "Đơn vị", span: { xs: 24, sm: 12, md: 8 } },
  { key: "trademark", label: "Thương hiệu", span: { xs: 24, sm: 12, md: 8 } },
];

export default function TrademarkCompanyMapping() {
  const { data: allListStore, loading: isLoadStoreAll } = useStoreData();
  const { listTrademark } = useSelector(
    (state) => state.persistedReducer.trademarkSlice
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branchFilter, setBranchFilter] = useState(null);
  const [trademarkFilter, setTrademarkFilter] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const branchOptions = useMemo(
    () => transformData(allListStore?.data || [])?.filter((i) => i.parent_id === 0),
    [allListStore]
  );
  const trademarkOptions = useMemo(
    () => (listTrademark || []).map((t) => ({ label: t.name, value: t.id })),
    [listTrademark]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await store.dispatch(
        getAllTrademarkCompanyMap({
          mcoupon_branch_id: branchFilter || undefined,
          trademark_id: trademarkFilter || undefined,
        })
      );
      if (getAllTrademarkCompanyMap.fulfilled.match(res)) {
        setRows(res.payload?.data || []);
      } else {
        message.error("Không tải được danh sách liên kết");
      }
    } finally {
      setLoading(false);
    }
  }, [branchFilter, trademarkFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    store.dispatch(getListTrademark({ currentPage: 1, pageSize: 1000 }));
  }, []);

  const handleToggle = async (record, checked) => {
    try {
      const res = await store.dispatch(
        updateTrademarkCompanyMapStatus({
          id: record.mcoupon_branch_id,
          data: { trademark_id: record.trademark_id, is_sent_sms: checked ? 1 : 0 },
        })
      );
      if (updateTrademarkCompanyMapStatus.fulfilled.match(res)) {
        message.success(checked ? "Đã chọn thương hiệu gửi SMS" : "Đã tắt thương hiệu này");
        fetchData();
      } else {
        message.error("Cập nhật trạng thái thất bại");
      }
    } catch (e) {
      message.error("Có lỗi xảy ra");
    }
  };

  const handleDelete = async (record) => {
    try {
      await store.dispatch(
        deleteTrademarkCompanyMap({
          mcoupon_branch_id: record.mcoupon_branch_id,
          trademark_id: record.trademark_id,
        })
      );
      message.success("Đã hủy liên kết");
      fetchData();
    } catch (e) {
      message.error("Hủy liên kết thất bại");
    }
  };

  const columns = [
    {
      title: "Đơn vị",
      dataIndex: "branch_name",
      render: (text) => <Text strong>{text || "—"}</Text>,
    },
    {
      title: "Thương hiệu SMS",
      dataIndex: "trademark_name",
      render: (text) => <StatusTag>{text || "—"}</StatusTag>,
    },
    {
      title: "Đang dùng gửi SMS",
      dataIndex: "is_sent_sms",
      align: "center",
      width: 160,
      render: (val, record) => (
        <Switch
          checked={val === 1}
          onChange={(checked) => handleToggle(record, checked)}
        />
      ),
    },
    {
      title: "Thao tác",
      key: "actions",
      align: "center",
      width: 100,
      render: (_, record) => (
        <RowActions
          items={[
            {
              key: "unlink",
              icon: ACTION_ICON.delete,
              label: "Hủy liên kết",
              danger: true,
              onClick: () =>
                confirmDanger({
                  title: "Hủy liên kết",
                  content: "Hủy liên kết thương hiệu này khỏi đơn vị?",
                  okText: "Hủy liên kết",
                  onOk: () => handleDelete(record),
                }),
            },
          ]}
        />
      ),
    },
  ];

  /*
   * Dải chỉ số đếm trên TOÀN BỘ `rows` — API này trả hết một lần (không phân
   * trang phía server) nên số liệu là thật, không phải đếm theo trang đang xem.
   */
  const stats = useMemo(() => {
    const active = rows.filter((r) => r.is_sent_sms === 1).length;
    const branches = new Set(rows.map((r) => r.mcoupon_branch_id)).size;
    return [
      {
        title: "Tổng liên kết",
        value: rows.length.toLocaleString("vi-VN"),
        icon: <ApiOutlined style={{ fontSize: 18 }} />,
        color: brand.accent,
      },
      {
        title: "Đang dùng gửi SMS",
        value: active.toLocaleString("vi-VN"),
        icon: <MessageOutlined style={{ fontSize: 18 }} />,
        color: semantic.success,
      },
      {
        title: "Đơn vị đã gán",
        value: branches.toLocaleString("vi-VN"),
        icon: <ShopOutlined style={{ fontSize: 18 }} />,
        color: semantic.info,
      },
    ];
  }, [rows]);

  return (
    <BrandedPageLayout
      title="Liên kết đơn vị & thương hiệu SMS"
      subtitle="Gán thương hiệu (brandname) SMS cho từng đơn vị. Mỗi đơn vị bật 1 thương hiệu đang dùng để gửi tin."
      breadcrumbItems={[
        { title: "Thương hiệu SMS", path: "/trademarks" },
        { title: "Liên kết đơn vị" },
      ]}
    >
      <StatStrip stats={stats} loading={loading && rows.length === 0} span={{ xs: 24, sm: 8 }} />

      <ListToolbar
        filterDefs={FILTER_DEFS}
        filters={{
          branch: (
            <TreeSelect
              style={{ width: "100%" }}
              value={branchFilter}
              disabled={isLoadStoreAll}
              placeholder="Lọc theo đơn vị"
              showSearch
              allowClear
              treeLine
              treeData={branchOptions}
              filterTreeNode={(input, node) =>
                node.title.toLowerCase().includes(input.toLowerCase())
              }
              onChange={(v) => setBranchFilter(v || null)}
            />
          ),
          trademark: (
            <Select
              style={{ width: "100%" }}
              value={trademarkFilter}
              placeholder="Lọc theo thương hiệu"
              allowClear
              showSearch
              optionFilterProp="label"
              options={trademarkOptions}
              onChange={(v) => setTrademarkFilter(v || null)}
            />
          ),
        }}
        onHideFilter={(key) => {
          if (key === "branch") setBranchFilter(null);
          if (key === "trademark") setTrademarkFilter(null);
        }}
        onClearFilters={() => {
          setBranchFilter(null);
          setTrademarkFilter(null);
        }}
        loading={loading}
        onReload={fetchData}
        actions={
          <Button
            type="primary"
            icon={<PlusCircleOutlined />}
            onClick={() => setIsModalOpen(true)}
          >
            Gán thương hiệu cho đơn vị
          </Button>
        }
      />

      <CmsTableCard
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        pagination={{
          showTotal: (total, range) =>
            `Hiển thị ${range[0]}–${range[1]} trong tổng ${total} liên kết`,
        }}
        locale={{ emptyText: <Empty description="Chưa có liên kết nào" /> }}
        scroll={{ x: "max-content" }}
        bordered
      />

      {isModalOpen && (
        <FormAddTrademarkCompanyMapping
          isModalOpen={isModalOpen}
          handleCancel={() => setIsModalOpen(false)}
          branchOptions={branchOptions}
          trademarkOptions={trademarkOptions}
          onSuccess={fetchData}
        />
      )}
    </BrandedPageLayout>
  );
}
